import RouteError from '@/components/ui/route-error';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import DashboardRouteLayout from '@/app/components/dashboard-route-layout';
import DashboardPageShell from '@/app/components/dashboard-page-shell';
import { DICTIONARY } from '@/config/dictionary';
import { requireAuth } from '@/utils/require-auth-before-load';
import { APP_PATHS } from '@/config/routes/app-paths';
import { ensureValidPath } from '@/utils/validate-path';
import SetupLayout from '@/features/wholesaler-setup/components/setup-layout';
import ActivateStep from '@/features/wholesaler-setup/components/steps/activate';
import BrandingStep from '@/features/wholesaler-setup/components/steps/branding';
import CompanyStep from '@/features/wholesaler-setup/components/steps/company';
import CreditStep from '@/features/wholesaler-setup/components/steps/credit';
import FeaturesStep from '@/features/wholesaler-setup/components/steps/features-step';
import RegionsCarriageStep from '@/features/wholesaler-setup/components/steps/regions-carriage';
import ServicesStep from '@/features/wholesaler-setup/components/steps/services';
import { useSetupStore } from '@/features/wholesaler-setup/stores/setup-store';
import type { SetupFormData } from '@/features/wholesaler-setup/types/setup';
import { useAppStore } from '@/stores/app-store';
import { queryClient } from '@/lib/query-client';
import { getCountriesQueryOptions } from '@/hooks/use-countries';
import { getServiceFeatureCatalogQueryOptions } from '@/hooks/use-service-feature-catalog';
import { getPlatformSettingsQueryOptions } from '@/hooks/use-platform-settings';

const DashboardSetupPage = () => {
  const currentStep = useSetupStore((state) => state.currentStep);
  const setStep = useSetupStore((state) => state.setStep);
  const updateFormData = useSetupStore((state) => state.updateFormData);
  const flowMode = useSetupStore((state) => state.flowMode);
  const fullName = useSetupStore((state) => state.formData.fullName);
  const businessEmail = useSetupStore((state) => state.formData.businessEmail);
  const currentUser = useAppStore((state) => state.currentUser);
  const hasAttachedWholesaler = (currentUser?.wholesalers.length ?? 0) > 0;
  const navigate = useNavigate();
  const setup = DICTIONARY.dashboard.setup;

  useEffect(() => {
    if (hasAttachedWholesaler && flowMode !== 'new-wholesaler' && currentStep !== 'activate') {
      void navigate({ to: ensureValidPath(APP_PATHS.overview) });
    }
  }, [hasAttachedWholesaler, flowMode, currentStep, navigate]);

  useEffect(() => {
    if (currentStep === 'personal-details') {
      setStep('company');
    }
  }, [currentStep, setStep]);

  useEffect(() => {
    if (currentUser == null) {
      return;
    }

    const nextData: Partial<SetupFormData> = {};

    if (fullName.length === 0) {
      nextData.fullName = currentUser.displayName;
    }

    if (businessEmail.length === 0) {
      nextData.businessEmail = currentUser.email;
    }

    if (Object.keys(nextData).length > 0) {
      updateFormData(nextData);
    }
  }, [currentUser, businessEmail, fullName, updateFormData]);

  const resolvedStep = currentStep === 'personal-details' ? 'company' : currentStep;

  return (
    <DashboardRouteLayout>
      <SetupLayout currentStep={resolvedStep}>
        <DashboardPageShell
          title={flowMode !== 'new-wholesaler' ? setup.heading : undefined}
          description={flowMode !== 'new-wholesaler' ? setup.body : undefined}
        >
          {resolvedStep === 'company' && <CompanyStep />}
          {resolvedStep === 'regions-carriage' && <RegionsCarriageStep />}
          {resolvedStep === 'services' && <ServicesStep />}
          {resolvedStep === 'features' && <FeaturesStep />}
          {resolvedStep === 'credit' && <CreditStep />}
          {resolvedStep === 'branding' && <BrandingStep />}
          {resolvedStep === 'activate' && <ActivateStep />}
        </DashboardPageShell>
      </SetupLayout>
    </DashboardRouteLayout>
  );
};

export const Route = createFileRoute('/setup')({
  beforeLoad: requireAuth,
  loader: async () => {
    const { selectedWholesalerId, currentUser } = useAppStore.getState();
    const wholesalerScope = selectedWholesalerId ?? currentUser?.wholesalers[0]?.id ?? 'default';
    await Promise.allSettled([
      queryClient.prefetchQuery(getCountriesQueryOptions()),
      queryClient.prefetchQuery(getServiceFeatureCatalogQueryOptions(wholesalerScope)),
      queryClient.prefetchQuery(getPlatformSettingsQueryOptions()),
    ]);
    return null;
  },
  component: DashboardSetupPage,
  errorComponent: RouteError,
});
